// apps/codeframe/src/chrome/timeline/TimelinePanel.tsx
import { useEffect, useRef, useState } from 'react';
import { Film, Play, Pause, Rewind, X } from 'lucide-react';
import { useCanvasStore } from '../../store/canvas-store';
import { lottieElementsOf } from '../../lottie/scan';
import { useTimelineStore, MIN_HEIGHT, MAX_HEIGHT } from './timeline-store';
import { useMotionScope } from './useMotionScope';
import { TimelineTransport } from './TimelineTransport';
import { driveLottie } from './lottieTransport';
import { TrackList } from './TrackList';
import { KeyframeLanes } from './KeyframeLanes';
import { TimelineRuler } from './TimelineRuler';
import { EasingPopover } from './EasingPopover';
import { MotionPromptBar } from './MotionPromptBar';
import type { Frame, MotionTimeline } from '../../store/types';

/** Empty placeholder timeline so the clip-lane dock renders for a lottie-only
 *  frame (no keyframe tracks; transport is lottie-only). */
const EMPTY_TIMELINE: MotionTimeline = { duration: 0, tracks: [], playback: {} };

function lottieCount(frame: Frame | undefined): number {
  return frame && 'html' in frame ? lottieElementsOf((frame as { html: string }).html).length : 0;
}

export function TimelinePanel() {
  const open = useTimelineStore((s) => s.open);
  const height = useTimelineStore((s) => s.height);
  const setHeight = useTimelineStore((s) => s.setHeight);
  const setOpen = useTimelineStore((s) => s.setOpen);
  const setRecording = useTimelineStore((s) => s.setRecording);
  const dispatch = useCanvasStore((s) => s.dispatch);
  const scope = useMotionScope();
  const scopeId = scope?.frameId;
  const dragRef = useRef<{ startY: number; startH: number } | null>(null);

  // R toggles record (unless typing); Esc disarms it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && useTimelineStore.getState().recording) {
        setRecording(false);
        return;
      }
      if ((e.key === 'r' || e.key === 'R') && !e.metaKey && !e.ctrlKey && !e.altKey) {
        if (isEditableTarget(e.target)) return;
        e.preventDefault();
        setRecording(!useTimelineStore.getState().recording);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setRecording]);

  // Disarm record when the dock closes or the scoped frame changes.
  useEffect(() => {
    if (!open || !scopeId) setRecording(false);
  }, [open, scopeId, setRecording]);

  if (!open) return null;

  const onResizeDown = (e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { startY: e.clientY, startH: height };
    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      const dy = dragRef.current.startY - ev.clientY; // drag up grows
      setHeight(dragRef.current.startH + dy);
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-30 flex flex-col bg-[var(--color-surface)] border-t border-[var(--color-chip-border)] shadow-[var(--shadow-2)] text-[var(--color-text)]"
      style={{ height: Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, height)) }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* resize handle */}
      <div
        className="h-1.5 shrink-0 cursor-ns-resize hover:bg-[var(--color-accent)]"
        style={{ minHeight: 6 }}
        onMouseDown={onResizeDown}
        role="separator"
        aria-label="Resize timeline"
      />
      {/* header */}
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-[var(--color-chip-border)] font-[family-name:var(--font-mono)] text-xs">
        <Film size={14} className="text-[var(--color-tool-fx,var(--color-accent))]" />
        <span className="font-medium">Motion</span>
        {scope?.frame && <span className="text-[var(--color-text-muted)]">· {scope.frame.id.slice(0, 8)}</span>}
        <button
          className="ml-auto flex items-center hover:text-[var(--color-accent)]"
          onClick={() => setOpen(false)}
          aria-label="Close timeline"
          title="Close (⌘T)"
        >
          <X size={14} />
        </button>
      </div>

      {/* body */}
      {!scope || !scope.canAnimate ? (
        <Empty msg="Select a single design frame to animate it." />
      ) : !scope.timeline ? (
        lottieCount(scope.frame) > 0 ? (
          <LottieOnlyBody frameId={scope.frameId} onInit={() => dispatch({ t: 'motion.init', frameId: scope.frameId })} />
        ) : (
          <FirstRunBody onInit={() => dispatch({ t: 'motion.init', frameId: scope.frameId })} />
        )
      ) : (
        <TimelineBody frameId={scope.frameId} timeline={scope.timeline} />
      )}

      {/* footer: describe motion in words — the agent builds it via motion_apply.
          Shown for any animatable frame, including the empty first-run state. */}
      {scope?.canAnimate && <MotionPromptBar frameId={scope.frameId} />}
    </div>
  );
}

/** First-run dock for an animatable frame with no timeline: lead with "describe
 *  what you want" (the prompt bar is the footer) plus a quiet manual link to
 *  start an empty timeline by hand. */
function FirstRunBody({ onInit }: { onInit: () => void }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
      <p className="text-xs text-[var(--color-text-muted)] font-[family-name:var(--font-mono)]">
        Describe the motion you want below — Viv builds it.
      </p>
      <button
        className="text-xs text-[var(--color-text-muted)] underline-offset-2 hover:text-[var(--color-accent)] hover:underline font-[family-name:var(--font-mono)]"
        onClick={onInit}
      >
        or add an empty timeline to edit by hand
      </button>
    </div>
  );
}

function TimelineBody({ frameId, timeline }: { frameId: string; timeline: MotionTimeline }) {
  const laneAreaRef = useRef<HTMLDivElement>(null);
  const [laneWidth, setLaneWidth] = useState(600);

  useEffect(() => {
    const el = laneAreaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setLaneWidth(el.clientWidth));
    ro.observe(el);
    setLaneWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  return (
    <>
      <TimelineTransport frameId={frameId} timeline={timeline} />
      <div className="flex flex-1 min-h-0">
        <TrackList frameId={frameId} timeline={timeline} />
        <div ref={laneAreaRef} className="flex min-w-0 flex-1 flex-col">
          <TimelineRuler frameId={frameId} timeline={timeline} width={laneWidth} />
          <KeyframeLanes frameId={frameId} timeline={timeline} width={laneWidth} />
        </div>
      </div>
      <EasingPopover frameId={frameId} timeline={timeline} />
    </>
  );
}

/** Dock for a frame that has lottie clips but no keyframe timeline yet:
 *  a lottie-only transport + read-only clip lanes (no ruler/keyframe lanes). */
function LottieOnlyBody({ frameId, onInit }: { frameId: string; onInit: () => void }) {
  return (
    <>
      <div className="flex items-center gap-3 px-3 py-1.5 border-b border-[var(--color-chip-border)] font-[family-name:var(--font-mono)] text-xs">
        <button onClick={() => driveLottie(frameId, 'seek', 0)} className="hover:text-[var(--color-accent)]" aria-label="Rewind" title="Rewind">
          <Rewind size={14} />
        </button>
        <button onClick={() => driveLottie(frameId, 'play')} className="hover:text-[var(--color-accent)]" aria-label="Play" title="Play">
          <Play size={14} />
        </button>
        <button onClick={() => driveLottie(frameId, 'pause')} className="hover:text-[var(--color-accent)]" aria-label="Pause" title="Pause">
          <Pause size={14} />
        </button>
        <span className="text-[var(--color-text-muted)]">lottie clips</span>
        <button
          onClick={onInit}
          className="ml-auto rounded border border-[var(--color-chip-border)] px-2 py-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-accent)]"
          title="Add a keyframe timeline to this frame"
        >
          + add motion
        </button>
      </div>
      <div className="flex flex-1 min-h-0">
        <TrackList frameId={frameId} timeline={EMPTY_TIMELINE} />
        <div className="flex min-w-0 flex-1 flex-col" />
      </div>
    </>
  );
}

function isEditableTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || el.isContentEditable;
}

function Empty({ msg }: { msg: string }) {
  return (
    <div className="flex flex-1 items-center justify-center text-xs text-[var(--color-text-muted)] font-[family-name:var(--font-sans)]">
      {msg}
    </div>
  );
}
